import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatGun, formatKisa, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import AraSec from '../ui/AraSec';
import Ipucu from '../ui/Ipucu';
import KpiKart, { KpiIzgara } from '../ui/KpiKart';
import { DURUM_AD, DurumRozet, HatirlatCekmece, KANAL_AD, TeslimRozeti, type HatirlatmaKaydi } from '../ui/HatirlatCekmece';
import WhatsappYanitlar from './hatirlatma/WhatsappYanitlar';

/**
 * BORÇ HATIRLATMA — carilere SMS + e-posta + WhatsApp.
 * Beş sekme: Borçlular · Onay bekleyenler · Yanıtlar (WhatsApp) · Geçmiş · Ayarlar. Kurallar sunucuda
 * (apps/api/src/borc-hatirlatma.ts); bu ekran yalnız gösterir ve tetikler.
 */
type HedefDurum = 'var' | 'yok' | 'gecersiz';
type Borclu = {
  id: number; code: string | null; name: string; phone: string | null; email: string | null;
  telefon: HedefDurum; eposta: HedefDurum; kapali: boolean; kapaliNot: string | null;
  toplamKurus: number; gecikenKurus: number; kalemAdet: number; gecikenAdet: number; enEskiVade: string | null; gecikmeGun: number;
  sonIleti: { kanal: 'SMS' | 'EPOSTA' | 'WHATSAPP'; durum: string; at: string } | null;
};
type Liste = {
  satirlar: Borclu[];
  ozet: { gecikenKurus: number; gecikenCari: number; toplamKurus: number; cariSayisi: number; bekleyenOnay: number; buAyGonderilen: number };
};
type Sablon = { sms: string; epostaKonu: string; epostaGovde: string };
type Ayar = {
  acik: boolean; kip: 'ONAY' | 'OTOMATIK'; kademeler: number[]; kanallar: { sms: boolean; eposta: boolean; whatsapp: boolean };
  saat: number; enAzKurus: number; firmaAd: string; iletisim: string; sablonlar: { yaklasan: Sablon; geciken: Sablon };
};
export type AyarVeri = {
  ayar: Ayar; varsayilan: Ayar; yerTutucular: { ad: string; aciklama: string }[]; smsHazir: boolean; epostaHazir: boolean; whatsappHazir: boolean;
  whatsapp: { saglayici: string | null; sablonlar: { tur: 'geciken' | 'yaklasan'; ad: string; durum: string; engel: string | null }[] };
  sonTarama: { gun?: string; at?: string; uretilen?: number; gonderilen?: number; deneme?: number; hata?: number } | null;
};
type Sekme = 'borclular' | 'onay' | 'yanitlar' | 'gecmis' | 'ayarlar';
const SEKMELER: [Sekme, string][] = [['borclular', 'Borçlular'], ['onay', 'Onay bekleyenler'], ['yanitlar', 'Yanıtlar'], ['gecmis', 'Geçmiş'], ['ayarlar', 'Ayarlar']];

const hataMetni = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);
const kademeAd = (k: number | null) => (k == null ? 'elle' : k < 0 ? `vadeden ${-k} gün önce` : k === 0 ? 'vade günü' : `vade + ${k} gün`);

function Bilgi({ metin, tehlike, kapat }: { metin: string; tehlike?: boolean; kapat?: () => void }) {
  return (
    <div className={`ccd-banner${tehlike ? ' ccd-banner-danger' : ''}`} role="status">
      <span className="ccd-banner-icon" aria-hidden="true">{tehlike ? '!' : '✓'}</span>
      <span style={{ flex: 1 }}>{metin}</span>
      {kapat && <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} aria-label="Kapat">✕</button>}
    </div>
  );
}

export default function BorcHatirlatma() {
  const { can } = useAuth();
  const [ara, setAra] = useSearchParams();
  const sekme = (SEKMELER.find(([id]) => id === ara.get('sekme'))?.[0] ?? 'borclular') as Sekme;
  const sekmeAc = (s: Sekme) => setAra(s === 'borclular' ? {} : { sekme: s }, { replace: true });

  const [ayar, setAyar] = useState<AyarVeri | null>(null);
  const [ozet, setOzet] = useState<Liste['ozet'] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [surum, setSurum] = useState(0);            // artınca sekmeler yeniden yükler
  const yenile = useCallback(() => setSurum((n) => n + 1), []);
  const [yanitSayisi, setYanitSayisi] = useState(0);   // okunmamış WhatsApp yanıtı

  useEffect(() => {
    api<AyarVeri>('/api/hatirlatma/ayar').then(setAyar).catch((e) => setHata(hataMetni(e, 'Ayar okunamadı')));
    api<{ okunmamis: number }>('/api/whatsapp/yanitlar').then((v) => setYanitSayisi(v.okunmamis)).catch(() => undefined);
  }, [surum]);

  if (!can('reminder.view')) {
    return <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>Bu ekran rolünüzde yok.</span></div>;
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Borç Hatırlatma <Ipucu id="borc-hatirlatma" /></h1>
          <div className="ccd-meta">
            Vadesi yaklaşan ve geçen borçlar için carilere SMS, e-posta ve WhatsApp
            {can('receivable.view') && <> · <Link to="/tahsilat">Tahsilat'a dön</Link></>}
          </div>
        </div>
        {ayar && (
          <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }} data-tur="hatirlatma-durum">
            <span className={`ccd-badge ${ayar.ayar.acik ? 'ccd-badge-success' : 'ccd-badge-warning'}`}
                  title={ayar.ayar.acik ? `Her iş günü saat ${String(ayar.ayar.saat).padStart(2, '0')}:00'dan sonra taranır` : 'Otomatik tarama kapalı; elle gönderim çalışır'}>
              <span className="ccd-badge-dot" />{ayar.ayar.acik ? (ayar.ayar.kip === 'ONAY' ? 'Servis açık · onayla gönderir' : 'Servis açık · kendiliğinden gönderir') : 'Servis kapalı'}
            </span>
            <span className={`ccd-badge ${ayar.smsHazir ? 'ccd-badge-success' : ''}`} title={ayar.smsHazir ? 'SMS sağlayıcısı bağlı' : 'SMS yapılandırılmadı: ileti kayda yazılır, gönderilmez'}>
              SMS: {ayar.smsHazir ? 'bağlı' : 'deneme kipi'}
            </span>
            <span className={`ccd-badge ${ayar.epostaHazir ? 'ccd-badge-success' : ''}`} title={ayar.epostaHazir ? 'E-posta sunucusu bağlı' : 'E-posta yapılandırılmadı: ileti kayda yazılır, gönderilmez'}>
              E-posta: {ayar.epostaHazir ? 'bağlı' : 'deneme kipi'}
            </span>
            <span className={`ccd-badge ${ayar.whatsappHazir ? 'ccd-badge-success' : ''}`} title={ayar.whatsappHazir ? `WhatsApp bağlı (${ayar.whatsapp.saglayici ?? ''})` : 'WhatsApp kurulmadı: ileti kayda yazılır, gönderilmez. Kurulum: Ayarlar › WhatsApp'}>
              WhatsApp: {ayar.whatsappHazir ? 'bağlı' : 'deneme kipi'}
            </span>
          </div>
        )}
      </div>

      {ozet && (
        <KpiIzgara tur="hatirlatma-ozet">
          <KpiKart ton="kirmizi" ikon="uyari" etiket="Toplam vadesi geçmiş" fihrist="borc-vadesi-gecmis" deger={formatKisa(ozet.gecikenKurus)} tam={formatTl(ozet.gecikenKurus)}
                   soluk={ozet.gecikenKurus === 0} ipucu="Vade günü geçmiş, ödenmemiş borçların toplamı. Bugün vadeli olanlar sayılmaz."
                   alt={`Toplam açık ${formatTl(ozet.toplamKurus)}`} />
          <KpiKart ton="mavi" ikon="kisi" etiket="Borçlu cari" fihrist="borc-vadesi-gecmis" deger={ozet.cariSayisi} soluk={ozet.cariSayisi === 0}
                   alt={`${ozet.gecikenCari} carinin vadesi geçmiş`} />
          <KpiKart ton="turuncu" ikon="saat" etiket="Bekleyen onay" fihrist="hatirlatma-onay" deger={ozet.bekleyenOnay} soluk={ozet.bekleyenOnay === 0} vurgu={ozet.bekleyenOnay > 0}
                   to="/tahsilat/hatirlatma?sekme=onay" alt={ozet.bekleyenOnay > 0 ? 'Onaylayınca gönderilir →' : 'Kuyruk boş'} />
          <KpiKart ton="yesil" ikon="onay" etiket="Bu ay gönderilen" fihrist="hatirlatma-onay" deger={ozet.buAyGonderilen} soluk={ozet.buAyGonderilen === 0}
                   to="/tahsilat/hatirlatma?sekme=gecmis" alt="Gerçekten giden iletiler" ipucu="Deneme kipinde kalan ve iptal edilen iletiler sayılmaz." />
        </KpiIzgara>
      )}

      {hata && <Bilgi metin={hata} tehlike kapat={() => setHata(null)} />}

      <div className="ccd-tabs" role="tablist" data-tur="hatirlatma-sekmeler">
        {SEKMELER.map(([id, ad]) => (
          <button key={id} role="tab" aria-selected={sekme === id} className="ccd-tab" onClick={() => sekmeAc(id)}>
            {ad}{id === 'onay' && ozet && ozet.bekleyenOnay > 0 ? ` (${ozet.bekleyenOnay})` : ''}{id === 'yanitlar' && yanitSayisi > 0 ? ` (${yanitSayisi})` : ''}
          </button>
        ))}
      </div>

      {/* Borçlular her zaman yüklenir: KPI şeridi ve Geçmiş'teki cari seçimi ondan beslenir. */}
      <Borclular gorunur={sekme === 'borclular'} surum={surum} yenile={yenile} ozetVer={setOzet} />
      {sekme === 'onay' && <Kuyruk surum={surum} yenile={yenile} ayar={ayar} />}
      {sekme === 'yanitlar' && <WhatsappYanitlar surum={surum} yenile={yenile} sayiVer={setYanitSayisi} />}
      {sekme === 'gecmis' && <Gecmis surum={surum} yenile={yenile} />}
      {sekme === 'ayarlar' && (ayar ? <HatirlatmaAyarlar veri={ayar} kaydedildi={yenile} /> : <div className="ccd-inline-note">Yükleniyor…</div>)}
    </>
  );
}

/* ─── Borçlular ──────────────────────────────────────────────────────────── */
type Sira = 'geciken' | 'toplam' | 'gun' | 'ad' | 'vade';

function HedefRozet({ durum, yok, gecersiz }: { durum: HedefDurum; yok: string; gecersiz: string }) {
  if (durum === 'var') return null;
  return <span className={`ccd-badge ${durum === 'yok' ? 'ccd-badge-warning' : 'ccd-badge-danger'}`}>{durum === 'yok' ? yok : gecersiz}</span>;
}

function Borclular({ gorunur, surum, yenile, ozetVer }: { gorunur: boolean; surum: number; yenile: () => void; ozetVer: (o: Liste['ozet']) => void }) {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [yalnizGecmis, setYalnizGecmis] = useState(false);
  const [sira, setSira] = useState<{ alan: Sira; yon: 'artan' | 'azalan' }>({ alan: 'geciken', yon: 'azalan' });
  const [veri, setVeri] = useState<Liste | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [acik, setAcik] = useState<number | null>(null);
  const [tariyor, setTariyor] = useState(false);

  useEffect(() => {
    let iptal = false;
    const t = setTimeout(() => {
      const p = new URLSearchParams({ sirala: sira.alan, yon: sira.yon, yalnizGecmis: yalnizGecmis ? '1' : '0' });
      if (q.trim()) p.set('q', q.trim());
      api<Liste>(`/api/hatirlatma/borclular?${p}`)
        .then((v) => { if (!iptal) { setVeri(v); ozetVer(v.ozet); setHata(null); } })
        .catch((e) => { if (!iptal) setHata(hataMetni(e, 'Liste alınamadı')); });
    }, q ? 250 : 0);
    return () => { iptal = true; clearTimeout(t); };
  }, [q, yalnizGecmis, sira, surum, ozetVer]);

  async function tara() {
    if (!window.confirm('Bugünün taraması şimdi yapılsın mı?\n\nKademesi gelen borçlar için iletiler "Onay bekleyenler"e yazılır; hiçbir şey kendiliğinden GÖNDERİLMEZ.')) return;
    setTariyor(true); setHata(null);
    try {
      const r = await api<{ uretilen: number; cari: number; atlananKapali: number; atlananHedefsiz: number; atlananKucuk: number; atlananSablon?: number }>('/api/hatirlatma/simdi-uret', { method: 'POST', body: '{}' });
      const atlanan = [r.atlananSablon && `${r.atlananSablon} caride WhatsApp iletisi üretilmedi (şablon onaylı değil)`, r.atlananKapali && `${r.atlananKapali} kapalı cari`, r.atlananHedefsiz && `${r.atlananHedefsiz} telefonu ve e-postası olmayan cari`, r.atlananKucuk && `${r.atlananKucuk} en az tutarın altında`].filter(Boolean).join(', ');
      setBilgi(`${r.uretilen ? `${r.cari} cari için ${r.uretilen} ileti onay kuyruğuna yazıldı.` : 'Kademesi gelen yeni borç yok; ileti üretilmedi.'}${atlanan ? ` Atlanan: ${atlanan}.` : ''}`);
      yenile();
    } catch (e) { setHata(hataMetni(e, 'Tarama yapılamadı')); } finally { setTariyor(false); }
  }

  if (!gorunur) return null;
  const Baslik = ({ alan, ad, sayi }: { alan: Sira; ad: string; sayi?: boolean }) => (
    <th className={`ccd-th${sayi ? ' ccd-td-num' : ''}`} aria-sort={sira.alan === alan ? (sira.yon === 'artan' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="ccd-borc-sirala" title="Sıralamak için tıklayın"
              onClick={() => setSira((s) => ({ alan, yon: s.alan === alan ? (s.yon === 'artan' ? 'azalan' : 'artan') : (alan === 'ad' || alan === 'vade' ? 'artan' : 'azalan') }))}>
        {ad}<span aria-hidden="true">{sira.alan === alan ? (sira.yon === 'artan' ? ' ↑' : ' ↓') : ''}</span>
      </button>
    </th>
  );

  return (
    <>
      <div className="ccd-borc-arac">
        <div className="ccd-search" style={{ flex: '1 1 260px', maxWidth: 420 }}>
          <span className="ccd-search-icon" aria-hidden="true">⌕</span>
          <input className="ccd-input" type="search" inputMode="search" enterKeyHint="search" autoComplete="off" placeholder="Cari adı, kodu, telefon ya da e-posta…" aria-label="Borçlularda ara" value={q} onChange={(e) => setQ(e.target.value)} data-tur="hatirlatma-ara" />
        </div>
        <label className="ccd-row" style={{ gap: 6 }}>
          <input type="checkbox" checked={yalnizGecmis} onChange={(e) => setYalnizGecmis(e.target.checked)} /> Yalnız vadesi geçmiş
        </label>
        <span className="ccd-spacer" />
        {can('reminder.send') && (
          <button className="ccd-btn ccd-btn-sm" onClick={() => void tara()} disabled={tariyor} data-tur="hatirlatma-tara"
                  title="Günlük taramayı şimdi yapar: iletiler onay kuyruğuna yazılır, gönderilmez">{tariyor ? 'Taranıyor…' : 'Şimdi tara'}</button>
        )}
      </div>
      {bilgi && <Bilgi metin={bilgi} kapat={() => setBilgi(null)} />}
      {hata && <Bilgi metin={hata} tehlike kapat={() => setHata(null)} />}

      {!veri ? <div className="ccd-inline-note">Yükleniyor…</div> : veri.satirlar.length === 0 ? (
        <div className="ccd-empty">{q || yalnizGecmis ? 'Bu süzgece uyan borçlu cari yok.' : 'Açık borcu olan cari yok.'}</div>
      ) : (
        <div className="ccd-table-wrap ccd-borc-tablo" data-tur="hatirlatma-tablo">
          <table className="ccd-table">
            <thead>
              <tr>
                <Baslik alan="ad" ad="Cari" />
                <Baslik alan="geciken" ad="Vadesi geçmiş" sayi />
                <Baslik alan="toplam" ad="Toplam açık" sayi />
                <th className="ccd-th ccd-td-num">Kalem</th>
                <Baslik alan="vade" ad="En eski vade" />
                <Baslik alan="gun" ad="Gecikme" sayi />
                <th className="ccd-th">Son ileti</th>
                <th className="ccd-th">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {veri.satirlar.map((s) => (
                <tr key={s.id} className="ccd-tr">
                  <td className="ccd-td ccd-borc-cari">
                    <div>{can('quote.view') ? <Link to={`/crm/${s.id}`} title="Cari kartı ve ekstre"><b>{s.name}</b></Link> : <b>{s.name}</b>}</div>
                    <div className="ccd-meta">{[s.code, s.phone, s.email].filter(Boolean).join(' · ') || 'iletişim bilgisi yok'}</div>
                    <div className="ccd-row" style={{ gap: 4, flexWrap: 'wrap' }}>
                      <HedefRozet durum={s.telefon} yok="telefon yok" gecersiz="telefon geçersiz" />
                      <HedefRozet durum={s.eposta} yok="e-posta yok" gecersiz="e-posta geçersiz" />
                      {s.kapali && <span className="ccd-badge" title={s.kapaliNot ?? 'Bu cariye otomatik hatırlatma gönderilmez'}>hatırlatma kapalı</span>}
                    </div>
                  </td>
                  <td className="ccd-td ccd-td-num">{s.gecikenKurus > 0 ? <b className="ccd-kpi-down">{formatTl(s.gecikenKurus)}</b> : <span className="ccd-muted">—</span>}</td>
                  <td className="ccd-td ccd-td-num">{formatTl(s.toplamKurus)}</td>
                  <td className="ccd-td ccd-td-num">{s.kalemAdet}</td>
                  <td className="ccd-td ccd-td-nowrap">{formatGun(s.enEskiVade)}</td>
                  <td className="ccd-td ccd-td-num ccd-td-nowrap">
                    {s.gecikmeGun > 0 ? <span className="ccd-kpi-down">{s.gecikmeGun} gün</span> : s.gecikmeGun === 0 ? 'bugün' : <span className="ccd-muted">{-s.gecikmeGun} gün var</span>}
                  </td>
                  <td className="ccd-td">
                    {s.sonIleti ? <><DurumRozet durum={s.sonIleti.durum} kisa /><div className="ccd-meta">{KANAL_AD[s.sonIleti.kanal]} · {formatTarih(s.sonIleti.at)}</div></> : <span className="ccd-muted">—</span>}
                  </td>
                  <td className="ccd-td ccd-td-nowrap">
                    <button className="ccd-btn ccd-btn-sm" onClick={() => setAcik(s.id)}>{can('reminder.send') ? 'Hatırlat' : 'Dökümü gör'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {acik != null && <HatirlatCekmece cariId={acik} kapat={() => setAcik(null)} degisti={yenile} />}
    </>
  );
}

/* ─── Kayıt tablosu (kuyruk ve geçmiş ortak) ─────────────────────────────── */
function Metin({ k }: { k: HatirlatmaKaydi }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="ccd-borc-metin">
      {k.konu && <div><b>{k.konu}</b></div>}
      <div className={acik ? 'ccd-borc-metin-acik' : 'ccd-borc-metin-kisa'}>{k.govde}</div>
      {k.govde.length > 110 && <button type="button" className="ccd-borc-bag" onClick={() => setAcik(!acik)}>{acik ? 'kısalt' : 'tamamını göster'}</button>}
    </div>
  );
}

function Kuyruk({ surum, yenile, ayar }: { surum: number; yenile: () => void; ayar: AyarVeri | null }) {
  const { can } = useAuth();
  const yazar = can('reminder.send');
  const [satirlar, setSatirlar] = useState<HatirlatmaKaydi[] | null>(null);
  const [secili, setSecili] = useState<Set<number>>(new Set());
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);

  useEffect(() => {
    api<{ satirlar: HatirlatmaKaydi[] }>('/api/hatirlatma/kuyruk?durum=BEKLIYOR')
      .then((v) => { setSatirlar(v.satirlar); setSecili((s) => new Set([...s].filter((id) => v.satirlar.some((k) => k.id === id)))); })
      .catch((e) => setHata(hataMetni(e, 'Kuyruk alınamadı')));
  }, [surum]);

  async function toplu(ne: 'onayla' | 'iptal', idler: number[]) {
    if (idler.length === 0) return;
    const soru = ne === 'onayla'
      ? `${idler.length} ileti onaylansın mı?\n\nHer ileti o anki borca göre yeniden hazırlanır ve hemen gönderilir. Borcu kapanmış cariye ileti gitmez.`
      : `${idler.length} ileti iptal edilsin mi? Bu kademe için yeniden ileti üretilmez.`;
    if (!window.confirm(soru)) return;
    setMesgul(true); setHata(null); setBilgi(null);
    try {
      if (ne === 'onayla') {
        const r = await api<{ ozet: { gonderildi: number; deneme: number; hata: number; iptal: number; atlanan: number } }>('/api/hatirlatma/toplu-onayla', { method: 'POST', body: JSON.stringify({ ids: idler }) });
        const o = r.ozet;
        setBilgi([o.gonderildi && `${o.gonderildi} ileti gönderildi`, o.deneme && `${o.deneme} ileti deneme kipinde kaldı (gönderilmedi)`, o.iptal && `${o.iptal} ileti borç kapandığı için gönderilmedi`,
          o.hata && `${o.hata} ileti hata aldı (Geçmiş'te görünür)`, o.atlanan && `${o.atlanan} ileti atlandı`].filter(Boolean).join(' · ') || 'İşlem tamam.');
      } else {
        const r = await api<{ ozet: { iptal: number; atlanan: number } }>('/api/hatirlatma/toplu-iptal', { method: 'POST', body: JSON.stringify({ ids: idler }) });
        setBilgi(`${r.ozet.iptal} ileti iptal edildi.`);
      }
      setSecili(new Set());
      yenile();
    } catch (e) { setHata(hataMetni(e, 'İşlem yapılamadı')); } finally { setMesgul(false); }
  }

  if (!satirlar) return hata ? <Bilgi metin={hata} tehlike /> : <div className="ccd-inline-note">Yükleniyor…</div>;
  const hepsi = satirlar.length > 0 && secili.size === satirlar.length;
  return (
    <>
      <div className="ccd-meta">
        Otomatik taramanın ürettiği, gönderilmek için onayınızı bekleyen iletiler. Onayladığınızda ileti o anki borca göre yeniden hazırlanır;
        borç kapandıysa gönderilmez. İptal ettiğiniz ileti o kademe için yeniden üretilmez.
      </div>
      {/* WhatsApp kanalı açık ve bağlı ama şablon onaylı değil: o türde ileti ÜRETİLMEZ — nedeni burada görünür. */}
      {ayar?.ayar.kanallar.whatsapp && ayar.whatsappHazir && ayar.whatsapp.sablonlar.filter((x) => x.engel).map((x) => (
        <div key={x.tur} className="ccd-banner ccd-banner-warning" role="status" data-tur="whatsapp-sablon-engel">
          <span className="ccd-banner-icon" aria-hidden="true">!</span>
          <span style={{ flex: 1 }}><b>{x.tur === 'geciken' ? 'Vadesi gelen / geçen' : 'Vadesi yaklaşan'} borçlar için WhatsApp iletisi üretilmiyor.</b> {x.engel}{' '}
            <Link className="ccd-link" to="/ayarlar/whatsapp#whatsapp-sablon">Ayarlar › WhatsApp</Link></span>
        </div>
      ))}
      {bilgi && <Bilgi metin={bilgi} kapat={() => setBilgi(null)} />}
      {hata && <Bilgi metin={hata} tehlike kapat={() => setHata(null)} />}
      {satirlar.length === 0 ? <div className="ccd-empty">Onay bekleyen ileti yok.</div> : (
        <>
          {yazar && (
            <div className="ccd-borc-arac">
              <span className="ccd-meta">{secili.size ? `${secili.size} ileti seçili` : 'Toplu işlem için satır seçin'}</span>
              <span className="ccd-spacer" />
              <button className="ccd-btn ccd-btn-sm" disabled={mesgul || secili.size === 0} onClick={() => void toplu('iptal', [...secili])}>Seçilenleri iptal et</button>
              <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={mesgul || secili.size === 0} onClick={() => void toplu('onayla', [...secili])}>
                {mesgul ? 'İşleniyor…' : `Seçilenleri onayla${secili.size ? ` (${secili.size})` : ''}`}
              </button>
            </div>
          )}
          <div className="ccd-table-wrap ccd-borc-tablo">
            <table className="ccd-table">
              <thead>
                <tr>
                  {yazar && <th className="ccd-th" style={{ width: 36 }}>
                    <input type="checkbox" aria-label="Hepsini seç" checked={hepsi} onChange={() => setSecili(hepsi ? new Set() : new Set(satirlar.map((k) => k.id)))} />
                  </th>}
                  <th className="ccd-th">Cari</th><th className="ccd-th">Kanal</th><th className="ccd-th">Kademe</th>
                  <th className="ccd-th ccd-td-num">Borç</th><th className="ccd-th">İleti</th>
                  {yazar && <th className="ccd-th">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {satirlar.map((k) => (
                  <tr key={k.id} className="ccd-tr">
                    {yazar && <td className="ccd-td">
                      <input type="checkbox" aria-label={`${k.cari} iletisini seç`} checked={secili.has(k.id)}
                             onChange={() => setSecili((s) => { const y = new Set(s); if (y.has(k.id)) y.delete(k.id); else y.add(k.id); return y; })} />
                    </td>}
                    <td className="ccd-td ccd-borc-cari"><b>{k.cari}</b></td>
                    <td className="ccd-td">{KANAL_AD[k.kanal]}<div className="ccd-meta ccd-borc-hedef">{k.hedef}</div></td>
                    <td className="ccd-td ccd-td-nowrap">{kademeAd(k.kademe)}<div className="ccd-meta">{k.tur === 'YAKLASAN' ? 'vadesi yaklaşıyor' : 'vadesi geldi'} · {formatTarih(k.createdAt)}</div></td>
                    <td className="ccd-td ccd-td-num"><b>{formatTl(k.borcKurus)}</b><div className="ccd-meta">{k.kalemler.length} kalem</div></td>
                    <td className="ccd-td"><Metin k={k} /></td>
                    {yazar && <td className="ccd-td ccd-td-nowrap">
                      <span className="ccd-borc-islem">
                        <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={mesgul} onClick={() => void toplu('onayla', [k.id])}>Onayla</button>
                        <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={mesgul} onClick={() => void toplu('iptal', [k.id])}>İptal</button>
                      </span>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function Gecmis({ surum, yenile }: { surum: number; yenile: () => void }) {
  const { can } = useAuth();
  const [durum, setDurum] = useState('gecmis');
  const [cari, setCari] = useState('');
  const [satirlar, setSatirlar] = useState<HatirlatmaKaydi[] | null>(null);
  const [cariler, setCariler] = useState<{ id: number; ad: string; alt: string | null }[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);

  useEffect(() => {
    const p = new URLSearchParams({ durum });
    if (cari) p.set('customerId', cari);
    api<{ satirlar: HatirlatmaKaydi[] }>(`/api/hatirlatma/kuyruk?${p}`).then((v) => {
      setSatirlar(v.satirlar);
      // Cari süzgeci: geçmişte iletisi olan cariler (yazdıkça aranır).
      if (!cari) setCariler((eski) => {
        const harita = new Map(eski.map((c) => [c.id, c]));
        for (const k of v.satirlar) harita.set(k.customerId, { id: k.customerId, ad: k.cari, alt: null });
        return [...harita.values()].sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));
      });
    }).catch((e) => setHata(hataMetni(e, 'Geçmiş alınamadı')));
  }, [durum, cari, surum]);

  async function yenidenDene(k: HatirlatmaKaydi) {
    if (!window.confirm(`${k.cari} için ${KANAL_AD[k.kanal]} iletisi yeniden denensin mi?`)) return;
    setHata(null); setBilgi(null);
    try {
      const r = await api<{ durum: string; hata: string | null }>(`/api/hatirlatma/${k.id}/onayla`, { method: 'POST', body: '{}' });
      setBilgi(`Sonuç: ${DURUM_AD[r.durum] ?? r.durum}${r.hata ? ` — ${r.hata}` : ''}`);
      yenile();
    } catch (e) { setHata(hataMetni(e, 'Yeniden denenemedi')); }
  }

  return (
    <>
      <div className="ccd-borc-arac">
        <div style={{ flex: '1 1 260px', maxWidth: 360 }}>
          <AraSec secenekler={cariler} deger={cari} degis={setCari} yerTutucu="Cariye göre süz: yazmaya başlayın…" />
        </div>
        <div className="ccd-tabs ccd-borc-suzgec" role="tablist" aria-label="Duruma göre süz">
          {([['gecmis', 'Tümü'], ['GONDERILDI', 'Gönderilen'], ['DENEME', 'Deneme'], ['HATA', 'Hata'], ['IPTAL', 'İptal']] as const).map(([id, ad]) => (
            <button key={id} role="tab" aria-selected={durum === id} className="ccd-tab" onClick={() => setDurum(id)}>{ad}</button>
          ))}
        </div>
      </div>
      {bilgi && <Bilgi metin={bilgi} kapat={() => setBilgi(null)} />}
      {hata && <Bilgi metin={hata} tehlike kapat={() => setHata(null)} />}
      {!satirlar ? <div className="ccd-inline-note">Yükleniyor…</div> : satirlar.length === 0 ? <div className="ccd-empty">Kayıt yok.</div> : (
        <div className="ccd-table-wrap ccd-borc-tablo">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Tarih</th><th className="ccd-th">Cari</th><th className="ccd-th">Kanal</th><th className="ccd-th">Kademe</th>
                <th className="ccd-th ccd-td-num">Borç</th><th className="ccd-th">Durum</th><th className="ccd-th">İleti</th><th className="ccd-th">Kim</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((k) => (
                <tr key={k.id} className="ccd-tr">
                  <td className="ccd-td ccd-td-nowrap">{formatTarih(k.gonderimAt ?? k.onayAt ?? k.createdAt)}</td>
                  <td className="ccd-td ccd-borc-cari">{can('quote.view') ? <Link to={`/crm/${k.customerId}`}><b>{k.cari}</b></Link> : <b>{k.cari}</b>}</td>
                  <td className="ccd-td">{KANAL_AD[k.kanal]}<div className="ccd-meta ccd-borc-hedef">{k.hedef}</div></td>
                  <td className="ccd-td ccd-td-nowrap">{kademeAd(k.kademe)}</td>
                  <td className="ccd-td ccd-td-num">{formatTl(k.borcKurus)}</td>
                  <td className="ccd-td">
                    <DurumRozet durum={k.durum} />
                    {k.kanal === 'WHATSAPP' && k.durum === 'GONDERILDI' && <div><TeslimRozeti durum={k.durum} teslimAt={k.teslimAt} okunduAt={k.okunduAt} /></div>}
                    {k.hata && <div className="ccd-meta">{k.hata}</div>}
                    {k.durum === 'HATA' && can('reminder.send') && <button type="button" className="ccd-borc-bag" onClick={() => void yenidenDene(k)}>yeniden dene</button>}
                  </td>
                  <td className="ccd-td"><Metin k={k} /></td>
                  <td className="ccd-td ccd-meta">{k.kaynak === 'ELLE' ? `elle · ${k.olusturan ?? '—'}` : `otomatik${k.onaylayan ? ` · onay: ${k.onaylayan}` : ''}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ─── Ayarlar ────────────────────────────────────────────────────────────── */
const ORNEK: Record<string, string> = {
  cari: 'Örnek Yapı Ltd. Şti.', tutar: '48.750,00 TL', gecikenTutar: '32.400,00 TL', yaklasanTutar: '16.350,00 TL', adet: '2', enEskiVade: '03.10.2026', gun: '7',
  dokum: '• Fatura ART2026000118 — vade 03.10.2026 — 20.400,00 TL (7 gün geçti)\n• Fatura ART2026000131 — vade 08.10.2026 — 12.000,00 TL (2 gün geçti)',
};
const doldur = (sablon: string, d: Record<string, string>) =>
  sablon.replace(/\{(\w+)\}/g, (hepsi, ad: string) => (ad in d ? d[ad] : hepsi)).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();

/** Ayarlar sekmesi; Ayarlar › Borç hatırlatma bölümü de bu bileşeni gösterir. */
export function HatirlatmaAyarlar({ veri, kaydedildi }: { veri: AyarVeri; kaydedildi: () => void }) {
  const { can } = useAuth();
  const yazar = can('reminder.settings');
  const [a, setA] = useState<Ayar>(veri.ayar);
  const [enAzTl, setEnAzTl] = useState(String(veri.ayar.enAzKurus / 100));
  const [yeniKademe, setYeniKademe] = useState('');
  const [tur, setTur] = useState<'geciken' | 'yaklasan'>('geciken');
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [deneme, setDeneme] = useState<{ kanal: 'SMS' | 'EPOSTA'; hedef: string }>({ kanal: 'SMS', hedef: '' });
  const [denemeSonuc, setDenemeSonuc] = useState<{ metin: string; tehlike: boolean } | null>(null);

  const degisti = useMemo(() => JSON.stringify({ ...a, enAzKurus: Math.round(Number(enAzTl || 0) * 100) }) !== JSON.stringify(veri.ayar), [a, enAzTl, veri.ayar]);
  const s = a.sablonlar[tur];
  const sablonYaz = (alan: keyof Sablon, deger: string) => setA({ ...a, sablonlar: { ...a.sablonlar, [tur]: { ...s, [alan]: deger } } });
  const ornek = { ...ORNEK, firma: a.firmaAd, iletisim: a.iletisim };
  const smsOrnek = doldur(s.sms, ornek).replace(/\s*\n\s*/g, ' ');

  function kademeEkle() {
    const n = Number(yeniKademe);
    if (yeniKademe.trim() === '' || !Number.isInteger(n) || n < -30 || n > 365) { setHata('Kademe −30 ile 365 arasında tam sayı olmalı (eksi: vadeden önce).'); return; }
    setHata(null);
    setA({ ...a, kademeler: [...new Set([...a.kademeler, n])].sort((x, y) => x - y) });
    setYeniKademe('');
  }

  async function kaydet() {
    if (a.acik && !veri.ayar.acik && !window.confirm(a.kip === 'OTOMATIK'
      ? 'Servis açılıyor ve kip OTOMATİK: kademesi gelen borçlar için iletiler ONAY BEKLEMEDEN gönderilecek.\n\nİlk taramada birikmiş her borçlu cariye birer ileti gider. Devam edilsin mi?'
      : 'Servis açılıyor: her iş günü kademesi gelen borçlar için iletiler "Onay bekleyenler"e yazılacak; siz onaylamadan gönderilmez. Devam edilsin mi?')) return;
    if (a.kip === 'OTOMATIK' && veri.ayar.kip !== 'OTOMATIK' && a.acik && veri.ayar.acik
      && !window.confirm('Kip OTOMATİK yapılıyor: iletiler bundan sonra onay beklemeden gönderilecek. Emin misiniz?')) return;
    setMesgul(true); setHata(null); setBilgi(null);
    try {
      await api('/api/hatirlatma/ayar', { method: 'PUT', body: JSON.stringify({ ...a, enAzKurus: Math.round(Number(enAzTl || 0) * 100) }) });
      setBilgi('Ayarlar kaydedildi.');
      kaydedildi();
    } catch (e) { setHata(hataMetni(e, 'Ayar kaydedilemedi')); } finally { setMesgul(false); }
  }

  async function denemeGonder() {
    setDenemeSonuc(null);
    try {
      const r = await api<{ durum: string }>('/api/hatirlatma/deneme', { method: 'POST', body: JSON.stringify(deneme) });
      setDenemeSonuc(r.durum === 'GONDERILDI'
        ? { metin: `Deneme iletisi ${deneme.hedef} adresine gönderildi. Ulaştığını karşı taraftan doğrulayın.`, tehlike: false }
        : { metin: `${KANAL_AD[deneme.kanal]} kanalı yapılandırılmamış: hiçbir şey gönderilmedi. Sunucudaki .env dosyasına kanal bilgileri eklenmeli.`, tehlike: true });
    } catch (e) { setDenemeSonuc({ metin: hataMetni(e, 'Deneme gönderilemedi'), tehlike: true }); }
  }

  const kanalSatiri = (ad: string, hazir: boolean, acik: boolean, degis: (v: boolean) => void) => (
    <label className="ccd-borc-kanal">
      <input type="checkbox" checked={acik} disabled={!yazar} onChange={(e) => degis(e.target.checked)} />
      <span><b>{ad}</b></span>
      <span className={`ccd-badge ${hazir ? 'ccd-badge-success' : 'ccd-badge-warning'}`}>{hazir ? 'bağlı — gerçekten gönderir' : 'yapılandırılmadı — deneme kipinde'}</span>
    </label>
  );

  return (
    <div className="ccd-stack">
      {!yazar && <div className="ccd-meta">Ayarları yalnız yönetici değiştirebilir; burada salt okunur görüyorsunuz.</div>}
      {bilgi && <Bilgi metin={bilgi} kapat={() => setBilgi(null)} />}
      {hata && <Bilgi metin={hata} tehlike kapat={() => setHata(null)} />}

      <div className="ccd-borc-ayar-izgara">
        <div className="ccd-card ccd-stack-sm" data-tur="hatirlatma-servis" id="hatirlatma-servis">
          <div className="ccd-fieldset-title">Servis</div>
          <div className="ccd-row-between">
            <div>
              <b>{a.acik ? 'Otomatik tarama açık' : 'Otomatik tarama kapalı'}</b>
              <div className="ccd-meta">Açıkken her iş günü belirlenen saatten sonra bir kez taranır. Kapalıyken yalnız elle gönderim çalışır.</div>
            </div>
            <button type="button" role="switch" aria-checked={a.acik} aria-label="Otomatik tarama" disabled={!yazar}
                    className={`ccd-switch${a.acik ? ' ccd-is-on' : ''}`} onClick={() => setA({ ...a, acik: !a.acik })} />
          </div>
          <div className="ccd-field">
            <span className="ccd-field-label">Gönderim kipi</span>
            <label className="ccd-borc-kanal"><input type="radio" name="kip" checked={a.kip === 'ONAY'} disabled={!yazar} onChange={() => setA({ ...a, kip: 'ONAY' })} />
              <span><b>Onaylı</b> — iletiler kuyruğa düşer, siz onaylayınca gider (önerilen)</span></label>
            <label className="ccd-borc-kanal"><input type="radio" name="kip" checked={a.kip === 'OTOMATIK'} disabled={!yazar} onChange={() => setA({ ...a, kip: 'OTOMATIK' })} />
              <span><b>Otomatik</b> — iletiler üretildiği anda gönderilir</span></label>
          </div>
          <div className="ccd-borc-iki">
            <label className="ccd-field">
              <span className="ccd-field-label">Tarama saati (İstanbul)</span>
              <input className="ccd-input" type="number" inputMode="numeric" min={0} max={23} step={1} value={a.saat} disabled={!yazar}
                     onChange={(e) => setA({ ...a, saat: Math.max(0, Math.min(23, Math.round(Number(e.target.value) || 0))) })} />
              <span className="ccd-field-hint">Yalnız hafta içi; hafta sonuna denk gelen kademe pazartesi üretilir.</span>
            </label>
            <label className="ccd-field">
              <span className="ccd-field-label">En az borç tutarı (₺)</span>
              <input className="ccd-input" type="number" inputMode="decimal" min={0} step="any" value={enAzTl} disabled={!yazar} onChange={(e) => setEnAzTl(e.target.value)} />
              <span className="ccd-field-hint">Bu tutarın altındaki borç için ileti üretilmez. 0 = sınır yok.</span>
            </label>
          </div>
          {veri.sonTarama?.at && (
            <div className="ccd-meta">Son otomatik tarama: {formatTarih(veri.sonTarama.at)} · {veri.sonTarama.uretilen ?? 0} ileti üretildi
              {veri.sonTarama.gonderilen ? ` · ${veri.sonTarama.gonderilen} gönderildi` : ''}</div>
          )}
        </div>

        <div className="ccd-card ccd-stack-sm" data-tur="hatirlatma-kademe" id="hatirlatma-kademe">
          <div className="ccd-fieldset-title">Kademeler</div>
          <div className="ccd-meta">Vadeye göre gün. Eksi sayı vadeden ÖNCE ("vadesi yaklaşıyor"), 0 vade günü, artı sayı vadeden SONRA. Her borç kalemi her kademeden en çok bir kez geçer.</div>
          <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {a.kademeler.map((k) => (
              <span key={k} className={`ccd-badge ${k < 0 ? 'ccd-badge-info' : ''}`}>
                {kademeAd(k)}
                {yazar && a.kademeler.length > 1 && (
                  <button type="button" className="ccd-borc-cikar" aria-label={`${kademeAd(k)} kademesini kaldır`} onClick={() => setA({ ...a, kademeler: a.kademeler.filter((x) => x !== k) })}>×</button>
                )}
              </span>
            ))}
          </div>
          {yazar && (
            <div className="ccd-row" style={{ gap: 6 }}>
              <input className="ccd-input" type="number" inputMode="numeric" step={1} style={{ width: 130 }} placeholder="gün (örn. 45)" value={yeniKademe}
                     onChange={(e) => setYeniKademe(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); kademeEkle(); } }} />
              <button type="button" className="ccd-btn ccd-btn-sm" onClick={kademeEkle}>Kademe ekle</button>
            </div>
          )}
          <div className="ccd-fieldset-title" style={{ marginTop: 8 }} id="hatirlatma-kanal">Kanallar</div>
          {kanalSatiri('SMS', veri.smsHazir, a.kanallar.sms, (v) => setA({ ...a, kanallar: { ...a.kanallar, sms: v } }))}
          {kanalSatiri('E-posta', veri.epostaHazir, a.kanallar.eposta, (v) => setA({ ...a, kanallar: { ...a.kanallar, eposta: v } }))}
          {kanalSatiri('WhatsApp', veri.whatsappHazir, a.kanallar.whatsapp, (v) => setA({ ...a, kanallar: { ...a.kanallar, whatsapp: v } }))}
          <div className="ccd-meta">WhatsApp iletisi onaylı şablonla gider; kurulum, şablonlar ve deneme iletisi: <Link className="ccd-link" to="/ayarlar/whatsapp">Ayarlar › WhatsApp</Link>.
            {veri.whatsapp.sablonlar.some((x) => x.engel) && veri.whatsappHazir && <span className="ccd-field-error"> Şablon onaylı değil: WhatsApp iletisi üretilmez.</span>}</div>
          <div className="ccd-meta">Deneme kipinde ileti kayda yazılır ("ne gidecekti" görünür) ama kimseye gitmez. Kanalı bağlamak için sunucu yöneticisinden kanal bilgilerini eklemesini isteyin.</div>
          {yazar && (
            <div className="ccd-stack-sm">
              <span className="ccd-field-label">Deneme iletisi gönder</span>
              <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <div className="ccd-tabs ccd-borc-suzgec" role="tablist" aria-label="Deneme kanalı">
                  {(['SMS', 'EPOSTA'] as const).map((k) => (
                    <button key={k} type="button" role="tab" aria-selected={deneme.kanal === k} className="ccd-tab" onClick={() => setDeneme({ kanal: k, hedef: '' })}>{KANAL_AD[k]}</button>
                  ))}
                </div>
                <input className="ccd-input" style={{ flex: '1 1 180px' }} placeholder={deneme.kanal === 'SMS' ? 'Kendi cep numaranız' : 'Kendi e-posta adresiniz'}
                       value={deneme.hedef} onChange={(e) => setDeneme({ ...deneme, hedef: e.target.value })} />
                <button type="button" className="ccd-btn ccd-btn-sm" disabled={deneme.hedef.trim().length < 3} onClick={() => void denemeGonder()}>Gönder</button>
              </div>
              {denemeSonuc && <Bilgi metin={denemeSonuc.metin} tehlike={denemeSonuc.tehlike} kapat={() => setDenemeSonuc(null)} />}
            </div>
          )}
        </div>
      </div>

      <div className="ccd-card ccd-stack-sm" data-tur="hatirlatma-sablon">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="ccd-fieldset-title" id="hatirlatma-sablon">İleti şablonları</div>
          <div className="ccd-tabs ccd-borc-suzgec" role="tablist" aria-label="Şablon türü">
            <button type="button" role="tab" aria-selected={tur === 'geciken'} className="ccd-tab" onClick={() => setTur('geciken')}>Vadesi gelen / geçen</button>
            <button type="button" role="tab" aria-selected={tur === 'yaklasan'} className="ccd-tab" onClick={() => setTur('yaklasan')}>Vadesi yaklaşan</button>
          </div>
        </div>
        <div className="ccd-borc-iki">
          <label className="ccd-field">
            <span className="ccd-field-label">Firma adı <span className="ccd-muted">{'{firma}'}</span></span>
            <input className="ccd-input" value={a.firmaAd} disabled={!yazar} maxLength={120} onChange={(e) => setA({ ...a, firmaAd: e.target.value })} />
          </label>
          <label className="ccd-field">
            <span className="ccd-field-label">İmza / iletişim satırı <span className="ccd-muted">{'{iletisim}'}</span></span>
            <input className="ccd-input" value={a.iletisim} disabled={!yazar} maxLength={300} placeholder="örn. Muhasebe · 0212 000 00 00 · muhasebe@firma.com" onChange={(e) => setA({ ...a, iletisim: e.target.value })} />
          </label>
        </div>
        <div className="ccd-borc-sablon">
          <div className="ccd-stack-sm">
            <label className="ccd-field">
              <span className="ccd-field-label">SMS metni</span>
              <textarea className="ccd-textarea" rows={4} value={s.sms} disabled={!yazar} onChange={(e) => sablonYaz('sms', e.target.value)} />
              <span className={smsOrnek.length > 300 ? 'ccd-field-error' : 'ccd-field-hint'}>
                Örnek veriyle {smsOrnek.length} karakter{smsOrnek.length > 300 ? ' — 300\'ü aşıyor; uzun SMS birden çok ileti olarak ücretlenir' : ' (önerilen en çok 300)'}
              </span>
            </label>
            <label className="ccd-field">
              <span className="ccd-field-label">E-posta konusu</span>
              <input className="ccd-input" value={s.epostaKonu} disabled={!yazar} onChange={(e) => sablonYaz('epostaKonu', e.target.value)} />
            </label>
            <label className="ccd-field">
              <span className="ccd-field-label">E-posta metni</span>
              <textarea className="ccd-textarea" rows={12} value={s.epostaGovde} disabled={!yazar} onChange={(e) => sablonYaz('epostaGovde', e.target.value)} />
            </label>
            {yazar && (
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ alignSelf: 'flex-start' }}
                      onClick={() => { if (window.confirm('Bu türün üç metni de varsayılana dönsün mü?')) setA({ ...a, sablonlar: { ...a.sablonlar, [tur]: veri.varsayilan.sablonlar[tur] } }); }}>
                Varsayılan metne dön
              </button>
            )}
          </div>
          <div className="ccd-stack-sm">
            <span className="ccd-field-label">Canlı önizleme <span className="ccd-muted">(örnek veriyle)</span></span>
            <div className="ccd-borc-onizleme"><div className="ccd-eyebrow">SMS</div>{smsOrnek}</div>
            <div className="ccd-borc-onizleme"><div className="ccd-eyebrow">E-posta</div><b>{doldur(s.epostaKonu, ornek)}</b>{'\n\n'}{doldur(s.epostaGovde, ornek)}</div>
            <span className="ccd-field-label">Yer tutucular</span>
            <div className="ccd-borc-yertutucu">
              {veri.yerTutucular.map((y) => (<div key={y.ad}><span className="ccd-mono">{y.ad}</span><span className="ccd-meta">{y.aciklama}</span></div>))}
            </div>
          </div>
        </div>
      </div>

      {yazar && (
        <div className="ccd-borc-arac">
          <span className="ccd-meta">{degisti ? 'Kaydedilmemiş değişiklik var.' : 'Değişiklik yok.'}</span>
          <span className="ccd-spacer" />
          <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={!degisti || mesgul} onClick={() => { setA(veri.ayar); setEnAzTl(String(veri.ayar.enAzKurus / 100)); }}>Vazgeç</button>
          <button className="ccd-btn ccd-btn-primary" disabled={!degisti || mesgul} onClick={() => void kaydet()} data-tur="hatirlatma-kaydet">{mesgul ? 'Kaydediliyor…' : 'Ayarları kaydet'}</button>
        </div>
      )}
    </div>
  );
}

